import { forwardRef, useImperativeHandle, useMemo, useState } from 'react'
import SavingIndicator from '../../components/SavingIndicator.jsx'
import { useAutosave } from '../../hooks/useAutosave.js'

/**
 * Vitals entry — a plain grid of numeric fields.
 *
 * Save cadence (generous on purpose — typing out "37" in the temperature
 * field shouldn't race with the network):
 *   1. On blur of any field (fast when the clinician is tab-cycling).
 *   2. Debounced 10 seconds after the last edit.
 *   3. On component unmount / tab switch / submit (belt-and-braces).
 *
 * SOURCE-OF-TRUTH: `value` seeds this form once, on mount; the local
 *   `local` state is authoritative thereafter. We do NOT re-sync from
 *   `value` on later renders. Re-applying a (possibly stale) autosave
 *   refetch used to overwrite digits the clinician was mid-typing —
 *   type "3", the save fires, the display snapped back, then "7"
 *   produced "3.07" instead of "37". EncounterEditor remounts this form
 *   when a different encounter is opened, which re-seeds it cleanly.
 */
const FIELDS = [
  { key: 'temperature_c', label: 'Temp (°C)', step: '0.1', min: 25, max: 45 },
  { key: 'heart_rate', label: 'HR (bpm)', step: '1', min: 20, max: 250 },
  { key: 'resp_rate', label: 'RR (bpm)', step: '1', min: 5, max: 80 },
  { key: 'bp_systolic', label: 'BP systolic', step: '1', min: 40, max: 260 },
  { key: 'bp_diastolic', label: 'BP diastolic', step: '1', min: 20, max: 200 },
  { key: 'spo2', label: 'SpO₂ (%)', step: '1', min: 40, max: 100 },
  { key: 'weight_kg', label: 'Weight (kg)', step: '0.1', min: 0, max: 400 },
  { key: 'height_cm', label: 'Height (cm)', step: '0.1', min: 20, max: 260 },
]

const VitalsForm = forwardRef(function VitalsForm(
  { value, disabled, onSave, saveState = 'idle' }, ref,
) {
  const [local, setLocal] = useState(value || {})

  // Stable key so useAutosave reacts only when content actually changes.
  const localKey = useMemo(() => JSON.stringify(local), [local])

  function commit() {
    if (disabled) return
    onSave(local)
  }

  const flush = useAutosave(localKey, commit, 10_000)

  useImperativeHandle(ref, () => ({ flush }), [flush])

  function update(key, v) {
    const n = v === '' ? null : Number(v)
    setLocal((curr) => ({ ...curr, [key]: n }))
  }

  function handleBlur() {
    // On blur, commit immediately — tabbing between fields is a strong
    // signal that the clinician is done with the current value.
    commit()
  }

  // Derived BMI — calculated from weight + height when both are
  // present. Pure FE computation; the underlying values are what the
  // backend stores. WHO classification cut-offs ladder the result
  // into a colour-coded chip so a doctor sees over/under at a glance.
  // Each tier uses a distinct hue (sky → emerald → amber → orange →
  // coral → rose) so the chip clearly differs across categories.
  const wKg = Number(local.weight_kg) || 0
  const hCm = Number(local.height_cm) || 0
  const bmi = wKg > 0 && hCm > 0
    ? wKg / Math.pow(hCm / 100, 2)
    : null
  const bmiCategory = bmi == null ? null
    : bmi < 18.5 ? { label: 'Underweight', range: '< 18.5',     cls: 'bg-sky-50 text-sky-700 border-sky-200' }
    : bmi < 25   ? { label: 'Normal',      range: '18.5 – 24.9', cls: 'bg-emerald-50 text-emerald-800 border-emerald-200' }
    : bmi < 30   ? { label: 'Overweight',  range: '25 – 29.9',   cls: 'bg-amber-50 text-amber-800 border-amber-200' }
    : bmi < 35   ? { label: 'Obese (I)',   range: '30 – 34.9',   cls: 'bg-orange-50 text-orange-700 border-orange-200' }
    : bmi < 40   ? { label: 'Obese (II)',  range: '35 – 39.9',   cls: 'bg-coral-50 text-coral-800 border-coral-200' }
    :              { label: 'Obese (III)', range: '≥ 40',        cls: 'bg-rose-50 text-rose-800 border-rose-200' }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="font-medium text-slate-800">Vitals</div>
        <SavingIndicator state={saveState} />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {FIELDS.map((f) => (
          <label key={f.key} className="block">
            <span className="text-[11px] uppercase tracking-wide text-slate-500">{f.label}</span>
            <input
              type="number"
              inputMode="decimal"
              step={f.step}
              min={f.min}
              max={f.max}
              disabled={disabled}
              value={local[f.key] ?? ''}
              onChange={(e) => update(f.key, e.target.value)}
              onBlur={handleBlur}
              className="mt-1 w-full border rounded px-2 py-2 text-sm disabled:bg-slate-50"
            />
          </label>
        ))}
      </div>

      {/* Derived BMI — read-only chip beside the inputs. Doctor doesn't
          enter BMI; the system derives it from weight + height and
          shows the WHO category + the actual numeric range so it's
          obvious which tier the value falls into. Hidden when either
          input is missing. */}
      {bmi != null && bmiCategory && (
        <div className="flex flex-wrap items-baseline gap-3 border-t border-slate-100 pt-3">
          <div>
            <span className="text-[11px] uppercase tracking-wide text-slate-500 mr-2">BMI</span>
            <span className="text-base font-bold tabular-nums text-slate-900">
              {bmi.toFixed(1)}
            </span>
            <span className="text-[11px] text-slate-400 ml-1">kg/m²</span>
          </div>
          <span className={
            'inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide ' +
            'border rounded-full px-2.5 py-0.5 ' + bmiCategory.cls
          }>
            <span>{bmiCategory.label}</span>
            <span className="font-mono normal-case opacity-70">{bmiCategory.range}</span>
          </span>
          <span className="text-[11px] text-slate-400">
            WHO classification · auto-calculated from weight ÷ height²
          </span>
        </div>
      )}

      {!disabled && (
        <div className="text-[11px] text-slate-400">
          Values save when you leave a field. If you pause mid-number, it will also save after 10 seconds.
        </div>
      )}
    </div>
  )
})

export default VitalsForm
